import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthPermission } from './auth-permission';
import { AuthRole } from './auth-role';
import { AuthSession } from './auth-session';

export interface AccessPolicy {
  permissions?: readonly AuthPermission[];
  permissionMatch?: 'all' | 'any';
  roles?: readonly AuthRole[];
}

export const authGuard: CanActivateFn = (_route, state) => {
  const session = inject(AuthSession);
  if (session.isAuthenticated()) {
    return true;
  }

  return inject(Router).createUrlTree(['/login'], {
    queryParams: { returnUrl: state.url },
  });
};

export function requireAccess(policy: AccessPolicy): CanActivateFn {
  return (_route, state) => {
    const session = inject(AuthSession);
    const router = inject(Router);

    if (!session.isAuthenticated()) {
      return router.createUrlTree(['/login'], { queryParams: { returnUrl: state.url } });
    }

    const hasRoles = !policy.roles?.length || session.hasAnyRole(policy.roles);
    const hasPermissions =
      !policy.permissions?.length ||
      (policy.permissionMatch === 'any'
        ? session.hasAnyPermission(policy.permissions)
        : session.hasAllPermissions(policy.permissions));

    return hasRoles && hasPermissions
      ? true
      : router.createUrlTree(['/access-denied'], { queryParams: { from: state.url } });
  };
}
